'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useApp } from '@/context/AppContext'
import { api } from '@/lib/api'
import { Store, Eye, EyeOff, CheckCircle } from 'lucide-react'

type Step = 'shop' | 'user' | 'done'

export default function SetupPage() {
  const { setSession } = useApp()
  const router = useRouter()

  const [step, setStep] = useState<Step>('shop')
  const [shopName, setShopName] = useState('')
  const [userName, setUserName] = useState('')
  const [pin, setPin] = useState('')
  const [showPin, setShowPin] = useState(false)
  const [saving, setSaving] = useState(false)

  async function finish() {
    if (!userName.trim() || pin.length !== 4) return
    setSaving(true)
    const { shop, user } = await api.setup(shopName, userName, pin)
    setSession({ shop, user })
    setStep('done')
    setTimeout(() => router.replace('/dashboard'), 1500)
    setSaving(false)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 to-indigo-800 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-white/20 rounded-3xl flex items-center justify-center mx-auto mb-3">
            <Store className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">Stoke</h1>
        </div>

        <div className="bg-white rounded-3xl p-6 shadow-2xl">
          <div className="flex gap-2 mb-6">
            {['shop','user'].map((s, i) => (
              <div key={s} className={`flex-1 h-1.5 rounded-full ${
                (s === 'shop' && step !== 'shop') || step === 'done' ? 'bg-indigo-600' :
                s === step ? 'bg-indigo-300' : 'bg-gray-200'
              }`} />
            ))}
          </div>

          {step === 'shop' && (
            <>
              <h2 className="text-xl font-bold text-gray-800 mb-1">Nom de la boutique</h2>
              <p className="text-gray-500 text-sm mb-5">Ach isem 7wentk?</p>
              <input autoFocus value={shopName} onChange={e => setShopName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && shopName.trim() && setStep('user')}
                placeholder="Ex: Magasin Ali..."
                className="w-full px-4 py-3.5 rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:bg-white focus:border-indigo-500 mb-4" />
              <button onClick={() => setStep('user')} disabled={!shopName.trim()}
                className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold text-lg disabled:opacity-50 active:scale-95">
                Suivant →
              </button>
            </>
          )}

          {step === 'user' && (
            <>
              <h2 className="text-xl font-bold text-gray-800 mb-1">Créer votre compte</h2>
              <p className="text-gray-500 text-sm mb-5">Compte Patron principal</p>
              <div className="space-y-3 mb-5">
                <input autoFocus value={userName} onChange={e => setUserName(e.target.value)}
                  placeholder="Votre prénom..."
                  className="w-full px-4 py-3.5 rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:bg-white focus:border-indigo-500" />
                <div className="relative">
                  <input type={showPin ? 'text' : 'password'} inputMode="numeric" maxLength={4}
                    value={pin} onChange={e => setPin(e.target.value.replace(/\D/g,'').slice(0,4))}
                    placeholder="PIN 4 chiffres..."
                    className="w-full px-4 py-3.5 pr-12 rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:bg-white focus:border-indigo-500 text-xl tracking-[0.5em]" />
                  <button onClick={() => setShowPin(!showPin)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <button onClick={finish} disabled={saving || !userName.trim() || pin.length !== 4}
                className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold text-lg disabled:opacity-50 active:scale-95">
                {saving ? '...' : 'Créer ✓'}
              </button>
            </>
          )}

          {step === 'done' && (
            <div className="text-center py-4">
              <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-gray-800">Mzyan! ✅</h2>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
